import { createContext, useContext } from 'react';
import type { JSX, ReactNode } from 'react';
/* LE GLYPHE DU FONDATEUR, pas un Streamline : la chaine de `link.svg`
   (`icons/custom/`). Il n'entre ici, et pas dans les vues, que pour une raison
   de GRAPHE : `TableView` -> `LinkedCard` -> `views/core/index` -> `TableView`
   fermerait un cycle si la marque vivait dans la carte liee. Ce fichier, lui,
   ne depend que de react, du contrat et de l'assembleur. */
import { IconLink } from '../icons/custom/index.js';
import type {
  ChoixDeReferenceDef,
  FieldMap,
  FieldRole,
  LinkedCardDef,
  LinkedDoor,
  ProjectedRow,
  RollupFieldDef,
  SortLevel,
  ViewBinding,
  ViewColumn,
} from '../views/contract.js';
/* LE COMPARATEUR DE L'ECRAN, ET PAS UN AUTRE : les deux surfaces de lien qui
   trient (la carte, la fenetre de choix) rangent leurs lignes exactement comme
   une liste ordinaire. Ce module ne depend que de react, du contrat et de
   l'assembleur ; `views/project.ts` n'importe rien d'ici, donc l'ajout ne ferme
   aucun cycle. */
import { compareCells, sortValueOf } from '../views/project.js';
/* LE NOM DU SAC SUR-MESURE, lu de l'assembleur et jamais reecrit : `customFields`
   ecrit ici en clair serait le meme fait a deux endroits, et le jour ou l'un
   des deux bouge, une valeur imposee se rangerait a cote du champ qu'elle vise. */
import { SAC_SUR_MESURE } from '../views/screens.js';

/**
 * LES LIENS ENTRE PORTES, cote SOCLE : le PORT, et les regles PURES qui vont
 * avec.
 *
 * CE QUE CE FICHIER NE FAIT PAS, ET POURQUOI. Il n'importe pas `@taki/sdk` et
 * il n'en importera jamais : l'ordre de build est sdk -> ui -> shell -> apps,
 * et le socle d'interface ne peut pas dependre de ce qui est construit apres
 * lui. C'est la meme frontiere que `lib/templates.tsx`, et le meme partage des
 * roles : le socle decrit la FORME du service (quatre verbes de LECTURE),
 * l'HOTE la remplit avec `sdk.repositoryOf(family)`, et la carte liee ne
 * connait que cette forme.
 *
 * CE QUE LE PORT SERT, ET RIEN D'AUTRE : « les interventions de CE materiel ».
 * Une porte declare ses LIENS (`liens: [{ id, porte, par }]`), une carte de
 * fiche en NOMME un, et le socle en tire une requete cadree par la fiche
 * ouverte. Le filtre se fait donc au SERVEUR (une egalite `where`), jamais en
 * memoire : charger une collection entiere pour la filtrer ensuite est
 * exactement le fork que le chantier refuse.
 *
 * LECTURE SEULE (PR 1), A UNE EXCEPTION QUI SE NOMME : `rattacher`. Ni
 * creation, ni archivage, et toujours aucune ecriture sur la fiche OUVERTE :
 * suivre un lien, c'est afficher, compter et totaliser. Le seul geste de la
 * carte qui ECRIT est « Lier une fiche existante », et il ecrit sur l'ENFANT
 * choisi, une seule clef, le champ de reference du lien (`link.by`). Ce n'est
 * pas une entorse a la promesse, c'est le SENS du lien : la reference vit dans
 * le document de celui qui DESIGNE, jamais dans celui qui est designe, donc
 * rattacher ne peut s'ecrire nulle part ailleurs. Cote SDK, `FamilyRepository`
 * promet `update` a cote des quatre lectures, et c'est tout ce que l'hote
 * appelle : une ligne, aucune connaissance de la famille.
 *
 * LE PORT EST FACULTATIF, comme celui des modeles : `useLiensPort()` rend
 * `null` quand personne ne le fournit (un banc d'essai, un test, un shell sans SDK),
 * et la carte liee rend alors un bloc GRISE avec sa raison (G16), jamais une
 * erreur. Une app montee sans hote n'est pas cassee, elle est incomplete, et
 * ca se lit.
 */

/**
 * LE CADRAGE D'UN LIEN : ce qui designe les fiches liees, et rien de plus.
 *
 * C'est ce que les TROIS agregats prennent, exactement comme `CountOptions`
 * cote SDK : un agregat prend le cadrage d'une liste, jamais sa fenetre. Un
 * total qui ne compterait que la page affichee mentirait des la premiere fiche
 * hors plafond (AGENTS.md section 7).
 */
export interface CadrageDeLien {
  /** Le sous-type du jeu lie, quand la porte liee en declare un. */
  type?: string;
  /** Les egalites du lien : `{ [lien.by]: id de la fiche ouverte }`. */
  where: Record<string, unknown>;
  /**
   * LES ARCHIVES DANS UN AGREGAT : absent (le defaut, les fiches ACTIVES seules,
   * inchange) ou `'all'`.
   *
   * QUI DEMANDE `'all'`, ET POURQUOI LUI SEUL. Le compte d'un rollup et le pied
   * d'un tableau parlent de ce que la carte MONTRE, c'est-a-dire des fiches
   * vivantes : ils ne demandent rien. Le REFUS DE SUPPRESSION, lui, compte ce
   * qui RESTERAIT ACCROCHE a une fiche effacee, et une intervention archivee y
   * est toujours accrochee : archiver n'est pas detacher. C'est le seul appelant
   * de cette clef (`comptesLies`, plus bas).
   *
   * `'only'` N'EXISTE PAS ICI, comme dans `ArgumentsDeListe` : personne n'a de
   * raison d'agreger UNIQUEMENT des archives, et une valeur qu'aucun appelant
   * n'emploie est une branche que rien ne verifie.
   *
   * LES SUPPRIMES NE SONT DANS AUCUN DES DEUX : depuis le 09/09/2026, le SDK
   * pose `deletedAt == null` au SERVEUR sur tout agregat hors `'exclude'`
   * (`aggregateFilters`, `packages/sdk/src/list-query.ts`). Sans ca, `'all'`
   * aurait compte la corbeille, et le refus aurait bloque une suppression que
   * plus rien ne justifie.
   */
  archived?: 'all';
}

/**
 * Le cadrage, plus la FENETRE : ce que la carte liee charge pour l'afficher.
 *
 * AUCUN TRI SERVEUR, ET C'EST UNE DECISION. Firestore EXCLUT d'une requete les
 * documents ou le champ d'un `orderBy` est absent. Sur un champ FACULTATIF (la
 * plupart des champs sur-mesure), `list` et `count` cesseraient alors de couvrir
 * le meme jeu : le compte dirait 3, la carte chargerait 2, elle s'annoncerait
 * « partielle » et afficherait « 2 / 3 » sans qu'aucune ligne ne manque
 * vraiment. Une carte VIDE a cote d'un compte a 3 est le meme defaut, en pire :
 * rien ne le signale.
 *
 * LE TRI EST DONC CLIENT, sur la fenetre chargee (au plus `limit`, donc au plus
 * 500 lignes) : la pile de l'ecran d'abord, puis les gestes de l'en-tete. Le
 * prix est connu et il est ecrit : au-dela du plafond, la fenetre est celle que
 * le serveur rend en ORDRE NATUREL, pas « les 200 plus recentes ».
 */
export interface ArgumentsDeListe {
  /** Le sous-type du jeu, quand la porte en declare un. */
  type?: string;
  /**
   * LES EGALITES, OU RIEN DU TOUT.
   *
   * Une CARTE LIEE cadre toujours (`{ [lien.by]: id de la fiche ouverte }`) :
   * charger un jeu entier pour le filtrer en memoire est exactement le fork que
   * le chantier refuse.
   *
   * UN ANNUAIRE, LUI, CHARGE TOUT UN JEU, et c'est son travail : il resout les
   * NOMS de toutes les fiches qu'une colonne de reference peut designer, donc
   * il n'a rien a cadrer d'autre que le sous-type. Une egalite y retiendrait
   * justement les fiches deja liees, c'est-a-dire l'inverse de ce qu'un choix
   * demande. C'est la seule raison pour laquelle `where` est facultatif ICI et
   * obligatoire sur les trois agregats : un total sans cadrage compterait le
   * jeu entier et rendrait un nombre plausible.
   */
  where?: Record<string, unknown>;
  /**
   * LES ARCHIVES : `'exclude'` (le defaut, quand la clef est absente) ou
   * `'all'`. MEME VOCABULAIRE QUE LE SDK (`ListOptions.archived`), et c'est
   * voulu : l'hote relaie la valeur telle quelle, sans table de traduction qui
   * inverserait un jour le sens d'un mot.
   *
   * QUI DEMANDE QUOI, ET POURQUOI. Une CARTE LIEE montre les fiches VIVANTES
   * d'une fiche parente : elle ne demande rien, donc `'exclude'`. Un ANNUAIRE
   * demande `'all'` : il RESOUT DES NOMS, et une reference peut pointer une
   * fiche archivee. Sans les archives, cette cellule dirait « Fiche liee
   * introuvable » sur une fiche qui existe toujours, ce qui envoie chercher une
   * suppression qui n'a pas eu lieu.
   *
   * `'only'` N'EXISTE PAS ICI, et ce n'est pas un oubli : personne n'a de
   * raison de resoudre UNIQUEMENT des archives, et une valeur qu'aucun appelant
   * n'emploie est une branche que rien ne verifie.
   */
  archived?: 'exclude' | 'all';
  /** Le plafond de la carte : ce qu'elle charge, jamais ce qui existe. */
  limit: number;
}

/**
 * CE QUE L'HOTE FOURNIT : quatre verbes de LECTURE sur une famille designee par
 * son NOM, plus UN verbe d'ecriture, `rattacher`.
 *
 * `family` EST UNE CHAINE, et c'est le fond du chantier : la famille du jeu lie
 * (`assets`, `careRecords`, au PLURIEL, comme `jeu.family`) n'est connue qu'a
 * l'execution. L'hote la resout par `sdk.repositoryOf(family)`, qui leve sur
 * une famille inconnue plutot que de rendre `undefined`.
 */
export interface LiensPort {
  /**
   * La fenetre de fiches : deja cadree et plafonnee.
   *
   * DEUX APPELANTS, DEUX CADRAGES : une CARTE LIEE demande les fiches d'UNE
   * fiche parente (`where`), un ANNUAIRE demande TOUT un jeu pour en resoudre
   * les noms (pas de `where`). Un seul verbe pour les deux : c'est la meme
   * lecture, et l'hote la sert avec le meme `sdk.repositoryOf(family).list`.
   */
  list(family: string, args: ArgumentsDeListe): Promise<Array<Record<string, unknown>>>;
  /**
   * Le total SERVEUR : le « 312 » de « 200 / 312 », le compte d'un rollup, et
   * le compte du REFUS DE SUPPRESSION (`comptesLies`, seul appelant a demander
   * `archived: 'all'`).
   */
  count(family: string, args: CadrageDeLien): Promise<number>;
  /** La somme d'un champ du jeu lie. Rend 0 quand aucune fiche ne repond. */
  sum(family: string, field: string, args: CadrageDeLien): Promise<number>;
  /**
   * La moyenne d'un champ du jeu lie. Rend `null` sans aucune fiche, et ce
   * `null` NE SE TRANSFORME PAS EN ZERO : « aucune fiche » et « moyenne nulle »
   * sont deux faits differents, et le second est un chiffre qu'on croirait.
   */
  average(family: string, field: string, args: CadrageDeLien): Promise<number | null>;
  /**
   * RATTACHE UNE FICHE QUI EXISTE DEJA : la fiche `id` de `family` recoit
   * `valeur` (l'id de la fiche ouverte) a son `chemin` (le `by` du lien).
   *
   * C'EST L'ENFANT QU'ON ECRIT, ET LUI SEUL. Une reference vit dans le document
   * de celui qui DESIGNE : rattacher une personne a une societe, c'est poser
   * l'id de la societe SUR la personne. La fiche ouverte, elle, ne bouge pas
   * d'un octet, et c'est pour ca que ce verbe ne porte pas son id ailleurs que
   * dans `valeur`.
   *
   * UNE REFERENCE EST A VALEUR UNIQUE : rattacher une fiche deja liee ailleurs
   * la DEPLACE, elle ne l'ajoute pas. C'est la SURFACE qui doit le dire avant le
   * clic (`candidatsARattacher`, mention « Deja liee ailleurs ») ; ce verbe-ci
   * ecrit ce qu'on lui demande, sans deviner.
   *
   * `chemin` EST UN CHEMIN DE STOCKAGE (`productId`,
   * `customFields.fournisseurId`), tel que l'assemblage l'a pose : l'hote le
   * range par `patchDeRattachement`, jamais a la main. Un champ sur-mesure pose
   * a la RACINE passe la validation et rend la fiche invisible de TOUTES les
   * cartes liees, sans la moindre erreur.
   *
   * IL NE REND RIEN : la carte ne relit pas la fiche ecrite, elle RECHARGE sa
   * liste et son compte au serveur. Rendre le document mis a jour aurait fait
   * croire qu'on peut s'en contenter, alors que le compte, lui, se demande.
   */
  rattacher(family: string, id: string, chemin: string, valeur: string): Promise<void>;
}

/**
 * LE CONTEXTE, DEFAUT `null`, et ce defaut est LU : un port absent est un fait
 * ordinaire, il grise la carte avec sa raison, il ne casse rien.
 */
const LiensContext = createContext<LiensPort | null>(null);

/**
 * Fournisseur pose par l'HOTE (celui qui a le SDK), autour du shell. Meme
 * patron que `ModelesProvider` : le socle decrit, l'exterieur remplit.
 */
export function LiensProvider({
  value,
  children,
}: {
  value: LiensPort | null;
  children: ReactNode;
}): JSX.Element {
  return <LiensContext.Provider value={value}>{children}</LiensContext.Provider>;
}

/** Le port des liens, ou `null` quand l'hote n'en fournit pas. */
export function useLiensPort(): LiensPort | null {
  return useContext(LiensContext);
}

/**
 * LA RAISON QUI ETEINT UNE CARTE LIEE, ECRITE ICI ET NULLE PART AILLEURS.
 *
 * Une seule, contrairement aux modeles qui en ont trois : les deux autres
 * manques possibles (le lien, la porte liee) sont refuses A L'ASSEMBLAGE, avec
 * leur nom (`views/screens.ts`). Ce qui reste est le seul manque qu'aucune
 * declaration ne peut couvrir : l'hote n'a pas monte de `LiensProvider`.
 */
export const RAISON_LIENS = {
  /** Personne n'a monte de `LiensProvider` au-dessus de l'ecran. */
  sansPort: "L'hote ne fournit pas les fiches liees.",
} as const;

/* ------------------------------------------------------------------ */
/* Le signe du lien : la marque posee a cote d'un libelle              */
/* ------------------------------------------------------------------ */

/**
 * LES DEUX MOTS DE LA MARQUE, ECRITS ICI ET NULLE PART AILLEURS.
 *
 * Ils ne sont pas interchangeables, et le NOMBRE est tout ce qui les separe :
 * une COLONNE de reference porte l'id d'UNE fiche d'ailleurs (« Fiche liee »),
 * le titre d'une CARTE liee annonce TOUTES les fiches d'ailleurs qui pointent
 * la fiche ouverte (« Fiches liees »). C'est le meme lien, lu dans ses deux
 * sens, et une infobulle qui dirait le meme mot des deux cotes effacerait
 * justement ce que la marque apprend.
 *
 * DES LIBELLES, PAS DES PHRASES (G26) : un nom nu, sans article ni verbe.
 */
export const LIBELLE_LIEN = {
  /** Le libelle d'une colonne de reference : la case designe UNE fiche. */
  fiche: 'Fiche liee',
  /** Le titre d'une carte liee : la carte montre LES fiches qui la designent. */
  carte: 'Fiches liees',
} as const;

/**
 * LA MARQUE DE LIEN : le petit signe qui dit « ceci n'est pas une valeur, c'est
 * une autre fiche ».
 *
 * POURQUOI ELLE EXISTE. Une colonne de reference ressemble a n'importe quelle
 * autre colonne : elle affiche un mot (« Sony FX9 »), et rien ne distingue ce
 * mot d'un texte saisi a la main. Le lecteur ne peut donc pas savoir qu'il y a
 * une fiche derriere, ni pourquoi ce champ s'edite par une liste plutot qu'au
 * clavier. La marque est ce que le socle a de moins cher pour le dire, et elle
 * le dit AU MEME ENDROIT dans les trois surfaces qui portent un lien : le
 * libelle d'un champ de fiche, l'en-tete d'une colonne, le titre d'une carte
 * liee.
 *
 * ELLE EST DE NATURE, PAS D'ETAT : elle ne change ni au survol, ni au tri, ni
 * quand la case s'ouvre. Son encre est donc toujours `ink-400`, l'encre des
 * libelles, jamais celle du texte actif : un signe qui s'allume se lit comme un
 * controle, et celui-ci ne se clique pas.
 *
 * ELLE NE PARLE PAS AUX LECTEURS D'ECRAN (`aria-hidden`), et c'est deliberent :
 * le nom accessible d'un en-tete de colonne est deja fixe (`aria-label`), et
 * une annonce « lien » sur chaque cellule ferait entendre un lien HTML la ou il
 * n'y en a aucun. Ce qu'elle porte est une INFOBULLE, pour l'oeil qui la
 * decouvre.
 *
 * `size` EST TOUJOURS DONNE PAR L'APPELANT : la marque se cale sur le texte
 * qu'elle accompagne (un libelle de champ, un en-tete en capitales, un titre de
 * carte n'ont pas la meme hauteur), et un defaut unique aurait fait grossir la
 * marque la ou le texte, lui, retrecit.
 */
export function MarqueDeLien({ titre, size }: { titre: string; size: number }): JSX.Element {
  return (
    <span title={titre} aria-hidden className="inline-flex shrink-0 text-ink-400">
      <IconLink size={size} />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Le sac, relu DEFENSIVEMENT                                          */
/* ------------------------------------------------------------------ */

/*
 * POURQUOI ON RELIT CE QUE LE SOCLE VIENT D'ASSEMBLER. Les cartes voyagent dans
 * le sac libre du binding (`ViewBinding.options`), qui est un
 * `Record<string, unknown>` : rien n'y est garanti par le type. Il vient
 * souvent de `bindingsDe` (assemble, donc juste), mais une app a parfaitement
 * le droit d'ecrire ses cartes a la main, et c'est le contrat. Une lecture
 * naive planterait la fiche ENTIERE sur une declaration approximative.
 *
 * ON SAUTE, ET ON LE DIT. Une entree invalide est ignoree (la fiche vit), mais
 * en developpement elle laisse une trace nommee : une carte qui disparait sans
 * un mot est exactement le genre de panne qu'on cherche pendant une heure.
 */

/** Un lien resolu, relu du sac : ses quatre noms, ou rien. */
function lienDuSac(valeur: unknown): LinkedDoor | null {
  if (typeof valeur !== 'object' || valeur === null) return null;
  const brut = valeur as Record<string, unknown>;
  const nom = (clef: string): string | null => {
    const lu = brut[clef];
    return typeof lu === 'string' && lu !== '' ? lu : null;
  };
  const id = nom('id');
  const door = nom('door');
  const by = nom('by');
  const family = nom('family');
  if (id === null || door === null || by === null || family === null) return null;
  const type = nom('type');
  return { id, door, by, family, ...(type === null ? {} : { type }) };
}

/** Les trois agregats du contrat, et rien d'autre. */
function agregatDuSac(valeur: unknown): 'count' | 'sum' | 'avg' | null {
  return valeur === 'count' || valeur === 'sum' || valeur === 'avg' ? valeur : null;
}

/**
 * UNE CARTE LIEE, relue du sac : l'objet TEL QUEL quand il tient debout.
 *
 * TEL QUEL, ET C'EST IMPORTANT : la carte garde son IDENTITE d'un rendu a
 * l'autre (le sac du binding, lui, est memoise par le moteur), et c'est cette
 * identite qui evite a la carte de recharger ses fiches liees a chaque frappe.
 */
export function carteLieeDuSac(valeur: unknown): LinkedCardDef | null {
  if (typeof valeur !== 'object' || valeur === null) return null;
  const brut = valeur as Record<string, unknown>;
  const titre = brut['title'];
  const vue = brut['view'];
  const plafond = brut['limit'];
  const binding = brut['binding'];
  const lien = lienDuSac(brut['link']);
  const debout =
    typeof titre === 'string' &&
    titre.trim() !== '' &&
    (vue === 'core.liste' || vue === 'core.tableau') &&
    typeof plafond === 'number' &&
    Number.isInteger(plafond) &&
    plafond > 0 &&
    typeof binding === 'object' &&
    binding !== null &&
    Array.isArray(brut['sort']) &&
    Array.isArray(brut['totals']) &&
    lien !== null;
  if (!debout) {
    /* `import.meta.env?.DEV` et non `import.meta.env.DEV` : ce module est le
       seul du chantier a tourner AUSSI hors navigateur (les tests `node --test`
       l'importent pour ses fonctions pures), et la, `import.meta.env` n'existe
       pas du tout. Sans le `?.`, la lecture leverait avant la garde. */
    if (import.meta.env?.DEV === true) {
      console.warn(
        `[taki/liens] Carte liee ignoree (${typeof titre === 'string' ? titre : 'sans titre'}) : ` +
          'il lui manque un titre, un lien resolu, une vue montable, un plafond, un binding, ' +
          'un tri ou ses totaux.',
      );
    }
    return null;
  }
  return valeur as LinkedCardDef;
}

/**
 * LES GESTES DES CHAMPS DE LIEN D'UNE FICHE, relus du sac
 * (`options.choixDeReference`) : une entree par clef de colonne, chacune TELLE
 * QUELLE quand elle tient debout.
 *
 * TELLE QUELLE, pour la meme raison que la carte (`carteLieeDuSac`) : son
 * identite est stable d'un rendu a l'autre, et la fenetre de choix ne relit
 * pas ses candidates a chaque frappe. Une entree bancale est ECARTEE seule : le
 * champ garde sa liste, sans les deux gestes, plutot que de faire tomber la
 * fiche.
 */
export function choixDeReferenceDuSac(valeur: unknown): Record<string, ChoixDeReferenceDef> {
  if (typeof valeur !== 'object' || valeur === null || Array.isArray(valeur)) return {};
  const retenus: Record<string, ChoixDeReferenceDef> = {};
  for (const [clef, brut] of Object.entries(valeur as Record<string, unknown>)) {
    const entree = typeof brut === 'object' && brut !== null ? (brut as Record<string, unknown>) : null;
    const retour = entree?.['retour'];
    const binding = entree?.['bindingChoix'];
    const debout =
      entree !== null &&
      typeof entree['family'] === 'string' &&
      entree['family'] !== '' &&
      typeof binding === 'object' &&
      binding !== null &&
      typeof retour === 'object' &&
      retour !== null &&
      typeof (retour as Record<string, unknown>)['family'] === 'string' &&
      typeof (retour as Record<string, unknown>)['chemin'] === 'string';
    if (debout) {
      retenus[clef] = brut as ChoixDeReferenceDef;
      continue;
    }
    if (import.meta.env?.DEV === true) {
      console.warn(
        `[taki/liens] Gestes du champ de lien "${clef}" ignores : il leur manque un jeu vise, ` +
          'un binding de choix ou le retour (jeu de la fiche, chemin du champ).',
      );
    }
  }
  return retenus;
}

/**
 * UN CHAMP DE TOTAL, relu du sac : `{ rollup: { label, link, agg, field? } }`,
 * ou rien. C'est ce qui distingue, dans les clefs d'une carte de champs, un
 * total d'un nom de champ nu.
 */
export function totalDuSac(valeur: unknown): RollupFieldDef | null {
  if (typeof valeur !== 'object' || valeur === null) return null;
  const enveloppe = (valeur as Record<string, unknown>)['rollup'];
  if (typeof enveloppe !== 'object' || enveloppe === null) return null;
  const brut = enveloppe as Record<string, unknown>;
  const label = brut['label'];
  const agg = agregatDuSac(brut['agg']);
  const lien = lienDuSac(brut['link']);
  if (typeof label !== 'string' || label.trim() === '' || agg === null || lien === null) {
    return null;
  }
  const champ = brut['field'];
  /* `count` NE PORTE AUCUN CHAMP (il compte des fiches), les deux autres en
     exigent un : sans lui, il n'y a rien a sommer. Le schema le refuse deja au
     poste de developpement ; ici on protege le rendu. */
  if (agg !== 'count' && (typeof champ !== 'string' || champ === '')) return null;
  /* L'HABILLAGE PASSE, quand l'assemblage en a pose un. Ce relecteur REBATIT
     l'objet clef par clef (c'est ce qui le rend sur) : une clef oubliee ici est
     une clef perdue en silence, et c'est ce qui affichait « 1890 » sous un
     total en euros. */
  const format = brut['format'];
  return {
    label,
    link: lien,
    agg,
    ...(typeof format === 'function' ? { format: format as (valeur: number) => string } : {}),
    ...(agg === 'count' || typeof champ !== 'string' ? {} : { field: champ }),
  };
}

/* ------------------------------------------------------------------ */
/* Ce qu'on demande au port : des arguments, pas une requete           */
/* ------------------------------------------------------------------ */

/**
 * LE CADRAGE D'UN LIEN, POSE UNE SEULE FOIS. La carte, le pied et les champs de
 * total le demandent tous les trois, et trois copies de `{ [by]: id }`
 * divergeraient le jour ou le lien porterait autre chose qu'une egalite.
 */
export function cadrageDeLien(lien: LinkedDoor, id: string): CadrageDeLien {
  return {
    ...(lien.type === undefined ? {} : { type: lien.type }),
    /* `by` EST DEJA UN CHEMIN DE STOCKAGE (`assetId`, `customFields.materielId`),
       pose par l'assemblage : on le prend TEL QUEL. Le rederiver ici en ferait
       la meme regle a deux endroits, et l'egalite d'un champ sur-mesure ne
       retiendrait rien, sans la moindre erreur. */
    where: { [lien.by]: id },
  };
}

/**
 * LES ARGUMENTS DE `list` POUR UNE CARTE LIEE : le cadrage du lien, et le
 * plafond de la carte. RIEN D'AUTRE, et surtout aucun `orderBy`.
 *
 * UNE EGALITE SEULE N'EXCLUT PERSONNE, un `orderBy` si : Firestore laisse de
 * cote les documents ou le champ trie est absent. La carte demanderait alors sa
 * fenetre a un jeu et son compte a un autre, et l'ecart se lirait comme un
 * plafond atteint (« 2 / 3 »), voire comme « Aucune fiche liee » a cote d'un
 * compte a 3. Aucune carte liee ne reclame donc d'index composite : c'est le
 * corollaire agreable de la meme decision.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est la seule chose de la carte qui
 * puisse se tromper en SILENCE (une egalite posee sur le mauvais champ rend une
 * carte vide, jamais une erreur), et un test ne peut la tenir que si elle est
 * atteignable sans React et sans SDK.
 */
export function argumentsDeListe<T>(carte: LinkedCardDef<T>, id: string): ArgumentsDeListe {
  return { ...cadrageDeLien(carte.link, id), limit: carte.limit };
}

/* ------------------------------------------------------------------ */
/* Supprimer une fiche liee : refuser, a la Odoo                       */
/* ------------------------------------------------------------------ */

/**
 * LE REFUS DE SUPPRIMER UNE FICHE QUI EN TIENT D'AUTRES (fondateur, 09/09/2026).
 *
 * LA DECISION, en une phrase : supprimer une fiche qui a des fiches liees est
 * REFUSE, avec le COMPTE, AVANT toute fenetre ; archiver ne l'est pas, mais la
 * confirmation dit combien de fiches restent accrochees. C'est le comportement
 * d'Odoo, et c'est le seul des trois possibles qui ne fabrique pas de donnee
 * orpheline : effacer en cascade detruit un travail que personne n'a demande
 * d'effacer, et detacher en silence laisse des interventions sans materiel que
 * plus aucun ecran ne remonte.
 *
 * UNE ARCHIVE COMPTE, UNE SUPPRIMEE NON. Archiver n'est pas detacher : une
 * intervention archivee pointe toujours son materiel, et l'effacer laisserait
 * exactement l'orphelin qu'on refuse. Une fiche supprimee, elle, n'existe plus
 * pour personne : elle n'entre dans aucun annuaire, et sa reference se dit
 * « Supprimee ou introuvable ». D'ou `archived: 'all'` et le
 * `deletedAt == null` que le SDK pose au serveur sur ce mode.
 *
 * AUCUNE DECLARATION NOUVELLE : la politique est le DEFAUT, pour toute porte qui
 * declare des liens. Une politique par lien (« celui-la, on detache ») viendra
 * si un cas reel la demande ; l'inventer avant aurait fait ecrire une clef que
 * personne ne sait remplir.
 */

/** Ce qu'une porte liee tient d'une fiche : son libelle, et le compte. */
export interface CompteLie {
  /** Le libelle de la porte liee, tel que le menu des portes l'affiche. */
  label: string;
  /** Les fiches liees, ARCHIVES COMPRISES, supprimees exclues. */
  count: number;
}

/**
 * LES COMPTES D'UNE FICHE, UN PAR LIEN, DEMANDES AU SERVEUR.
 *
 * EN PARALLELE, et pas l'un apres l'autre : ces comptes vivent entre le clic et
 * la fenetre, donc leur duree est celle du plus LENT, pas celle de leur somme.
 * Une porte qui declare quatre liens ne fait pas attendre quatre fois.
 *
 * IL NE RATTRAPE AUCUNE ERREUR, et c'est voulu : un `count` qui echoue doit
 * REFUSER la suppression (on ne prouve pas une absence avec une lecture qui n'a
 * pas eu lieu), et c'est l'appelant qui sait poser ce refus a l'ecran. Avaler
 * l'echec en rendant zero ferait exactement l'inverse : la suppression
 * passerait.
 *
 * `labelDePorte` VIENT DE L'APPELANT parce que le socle des liens ne connait pas
 * les portes de l'ecran : il traduit un `link.door` en libelle
 * (`ScreenDoor.label`), avec repli sur l'id.
 */
export async function comptesLies(
  port: LiensPort,
  links: readonly LinkedDoor[],
  id: string,
  labelDePorte: (door: string) => string,
): Promise<CompteLie[]> {
  return await Promise.all(
    links.map(async (lien) => ({
      label: labelDePorte(lien.door),
      count: await port.count(lien.family, { ...cadrageDeLien(lien, id), archived: 'all' }),
    })),
  );
}

/**
 * LE TOTAL ET LES PORTES NON NULLES, dans l'ordre des liens.
 *
 * LES DOUBLONS TOMBENT : deux liens peuvent viser la MEME porte par deux champs
 * (un materiel « pose » et un materiel « repris »), et « (Interventions,
 * Interventions) » se lit comme une faute d'affichage. Le total, lui, les
 * additionne : ce sont bien deux fiches accrochees.
 */
function bilanDesLies(comptes: readonly CompteLie[]): { total: number; portes: string[] } {
  let total = 0;
  const portes: string[] = [];
  for (const compte of comptes) {
    if (compte.count <= 0) continue;
    total += compte.count;
    if (!portes.includes(compte.label)) portes.push(compte.label);
  }
  return { total, portes };
}

/** « 3 fiches liees (Interventions) » / « 1 fiche liee (Interventions) ». */
function fichesLiees(total: number, portes: readonly string[]): string {
  const nom = total > 1 ? `${total} fiches liees` : '1 fiche liee';
  return `${nom} (${portes.join(', ')})`;
}

/**
 * LA RAISON DU REFUS, ou `null` quand rien n'est accroche.
 *
 * ELLE DIT LE COMPTE ET LES DEUX SORTIES (G16 : un geste refuse dit ce qui le
 * leve). « Archive cette fiche » est la sortie qui garde tout, « rattache-les
 * ailleurs » celle qui libere : deux gestes que l'utilisateur sait faire, pas
 * une explication de mecanique interne.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : l'accord (« 1 fiche liee ... rattache-la »
 * contre « 3 fiches liees ... rattache-les ») ne se voit qu'a l'ecran, et
 * seulement le jour ou une fiche n'en tient qu'une.
 */
export function refusDeSuppression(comptes: readonly CompteLie[]): string | null {
  const { total, portes } = bilanDesLies(comptes);
  if (total === 0) return null;
  const pronom = total > 1 ? 'rattache-les' : 'rattache-la';
  return `Suppression refusee : ${fichesLiees(total, portes)}. Archive cette fiche, ou ${pronom} ailleurs.`;
}

/**
 * LE BOUT DE PHRASE QUE LA CONFIRMATION D'ARCHIVAGE AJOUTE, ou `''` quand rien
 * n'est accroche.
 *
 * ARCHIVER N'EST PAS REFUSE, et cette phrase dit pourquoi ca ne casse rien : les
 * fiches liees RESTENT VISIBLES, elles ne partent pas avec le parent. C'est la
 * seule question qu'on se pose devant ce bouton quand une carte liee est ouverte
 * juste dessous.
 */
export function phraseDesLies(comptes: readonly CompteLie[]): string {
  const { total, portes } = bilanDesLies(comptes);
  if (total === 0) return '';
  const verbe = total > 1 ? 'restent visibles' : 'reste visible';
  return `${fichesLiees(total, portes)} ${verbe}.`;
}

/* ------------------------------------------------------------------ */
/* Ce qu'on donne a la vue montee dans la carte                        */
/* ------------------------------------------------------------------ */

/**
 * LE MEME BINDING, SANS UNE SEULE ECRITURE.
 *
 * UNE CARTE LIEE EST EN LECTURE (PR 1), et ca ne se tient pas en n'appelant pas
 * `onPatchRecord` : ce qui ouvre une case, c'est `ProjectedRow.editable` et
 * `cells[].editable`, tous deux calcules par la projection sur la PRESENCE d'un
 * `set()`. Retirer les `set` du binding avant de projeter est donc la seule
 * facon de rendre une case reellement inerte : sans ca, la fiche liee du
 * tableau proposerait ses editeurs, et une ecriture partirait sur un jeu que
 * cette carte ne sait pas recharger.
 *
 * NE MUTE RIEN : le binding vient de l'assemblage de la porte liee, que le
 * moteur emploie aussi pour l'ecran de cette porte. Le muter y eteindrait
 * l'edition, a distance.
 */
export function sansEcriture<T>(binding: ViewBinding<T>): ViewBinding<T> {
  const roles: Partial<Record<FieldRole, FieldMap<T>>> & Record<string, FieldMap<T>> = {};
  for (const [nom, champ] of Object.entries(binding.roles) as [string, FieldMap<T>][]) {
    if (champ == null) continue;
    const { set: _ecriture, ...lecture } = champ;
    roles[nom] = lecture;
  }
  const nu: ViewBinding<T> = { type: binding.type, roles };
  if (binding.columns !== undefined) {
    nu.columns = binding.columns.map((colonne) => {
      const { set: _ecriture, ...lecture } = colonne;
      return lecture;
    });
  }
  if (binding.options !== undefined) nu.options = binding.options;
  return nu;
}

/**
 * LA COLONNE QUI REDIT LE TITRE DE LA FICHE OUVERTE, RETIREE.
 *
 * LE FAIT. Le binding d'une carte liee est celui de la porte LIEE, references
 * comprises : sur la fiche d'un materiel, la carte « Interventions » monte le
 * tableau des interventions, et ce tableau declare la colonne « Materiel ».
 * Chaque ligne y afficherait le nom du materiel SOUS LEQUEL ON EST DEJA. Une
 * colonne dont toutes les cases portent le titre ecrit juste au-dessus
 * n'apprend rien et mange la largeur des colonnes qui, elles, disent quelque
 * chose.
 *
 * SUR LA PORTE, PAS SUR LE CHAMP : c'est `reference.door` qu'on compare a la
 * porte hote (`LinkedCardDef.porte`). Le champ du lien (`link.by`) dirait
 * presque la meme chose et se tromperait des qu'une porte designe une autre
 * porte par un second champ.
 *
 * LES AUTRES REFERENCES RESTENT, et elles sont le vrai gain de l'etage 1 : une
 * intervention qui designerait aussi un chantier montre le NOM de ce chantier.
 *
 * NE MUTE RIEN, et rend le binding TEL QUEL quand il n'y a rien a retirer :
 * l'identite est ce qui evite a la carte de reprojeter a chaque rendu.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : une colonne de trop est un defaut qu'on lit
 * sans le voir (« pourquoi cette colonne est-elle toujours la meme ? »), et
 * aucun build ne la compte.
 */
export function sansColonneVersLaPorte<T>(
  binding: ViewBinding<T>,
  porte: string,
): ViewBinding<T> {
  if (porte === '' || binding.columns === undefined) return binding;
  const gardees = binding.columns.filter((colonne) => colonne.reference?.door !== porte);
  if (gardees.length === binding.columns.length) return binding;
  return { ...binding, columns: gardees };
}

/**
 * LE TITRE QUI REDIT LA FICHE OUVERTE : celui d'une ligne liee qui designe la
 * porte hote (la tache d'une saisie, sur la fiche de cette tache). Meme regle
 * que `sansColonneVersLaPorte`, pour le role `title` : la carte le masque
 * (`hideTitle`, `TableView`), la ligne s'ouvre toujours au clic.
 */
export function titreVersLaPorte<T>(binding: ViewBinding<T>, porte: string): boolean {
  const parts = binding.roles.title?.parts;
  return porte !== '' && parts !== undefined && parts.length === 1 && parts[0]?.reference?.door === porte;
}

/**
 * LES COLONNES QUI REDISENT CE QUE LA FICHE OUVERTE ECRIT DEJA, RETIREES.
 *
 * LE FAIT (retour du fondateur du 14/10/2026, fiche d'une tache) : la carte
 * « Heures » d'une tache montrait le Projet de chaque saisie, ecrit en toutes
 * lettres juste au-dessus, sur la fiche. Une colonne de REFERENCE est retiree
 * quand la fiche ouverte designe la meme porte et que CHAQUE ligne chargee y
 * porte la meme fiche qu'elle : c'est la donnee qui le dit, jamais un nom de
 * champ. Une seule ligne differente, ou aucune ligne, et la colonne reste.
 *
 * @param redites la fiche ouverte : porte designee -> id de la fiche designee.
 */
export function sansColonnesRedites(
  colonnes: ViewColumn[],
  lignes: readonly ProjectedRow[],
  redites: Readonly<Record<string, string>>,
): ViewColumn[] {
  if (lignes.length === 0) return colonnes;
  const gardees = colonnes.filter((colonne) => {
    const porte = colonne.reference?.door;
    const id = porte === undefined ? undefined : redites[porte];
    return id === undefined || !lignes.every((ligne) => ligne.cells[colonne.key]?.value === id);
  });
  return gardees.length === colonnes.length ? colonnes : gardees;
}

/**
 * LES LIGNES, TRIEES PAR LA PILE : le niveau 1 commande, les suivants
 * departagent ses egalites, et l'ordre d'arrivee fait foi sur les ex aequo
 * (`Array.prototype.sort` est stable).
 *
 * MEME COMPARATEUR QUE L'ECRAN (`sortValueOf` + `compareCells`) : une clef que
 * le binding ne resout pas ne deplace rien, exactement comme dans une liste
 * ordinaire. Le tri par en-tete du tableau arrive ici, et nulle part ailleurs :
 * ni la carte liee ni la fenetre de choix ne rechargent, elles retrient ce
 * qu'elles ont.
 *
 * ELLE VIT DANS LE SOCLE DES LIENS, pas dans la carte, depuis que la FENETRE DE
 * CHOIX trie elle aussi ses candidates : deux copies « identiques au
 * comparateur pres » auraient range deux tables de la meme porte dans deux
 * ordres differents, le jour ou l'une des deux est retouchee.
 *
 * NE MUTE RIEN, et rend le tableau TEL QUEL quand il n'y a rien a trier :
 * l'identite est ce qui evite un rendu de plus.
 */
export function trierLignes(
  rows: ProjectedRow[],
  pile: readonly SortLevel[],
): ProjectedRow[] {
  if (pile.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const niveau of pile) {
      const compare = compareCells(sortValueOf(a, niveau.key), sortValueOf(b, niveau.key));
      if (compare !== 0) return niveau.dir === 'desc' ? -compare : compare;
    }
    return 0;
  });
}

/**
 * LES COLONNES TELLES QUE LA VUE LES RECOIT : les metadonnees, jamais les
 * fonctions.
 *
 * UNE SEULE TRADUCTION POUR TOUT LE PAQUET : le moteur d'ecran l'appelle aussi
 * (`views/useScreen.ts`, memo `projected`). Elle a vecu en deux copies, « la
 * meme au champ pres », jusqu'a ce que la REFERENCE d'une colonne doive
 * traverser la frontiere : une seule des deux l'aurait posee, et la fiche
 * aurait su servir son annuaire la ou le tableau ne l'aurait pas su.
 *
 * LA REFERENCE PASSE EN DONNEE, PAS EN FONCTION : `titleOf` et `subtitleOf`
 * restent cote core (ils remplissent l'annuaire), la vue ne recoit que de quoi
 * retrouver cet annuaire et nommer la porte de la fiche liee.
 */
export function colonnesDeVue<T>(binding: ViewBinding<T> | undefined): ViewColumn[] {
  return (binding?.columns ?? []).map((colonne) => ({
    key: colonne.key,
    label: colonne.label,
    sortable: colonne.sortable ?? false,
    ...(colonne.agg === undefined ? {} : { agg: colonne.agg }),
    /* L'HABILLAGE DE L'AGREGAT VOYAGE AVEC LUI : sans cette ligne, le pied d'une
       colonne en euros retombait sur le nombre nu, six lignes sous des cases
       qui portent le signe. */
    ...(colonne.formatAgg === undefined ? {} : { formatAgg: colonne.formatAgg }),
    ...(colonne.totalLie === true ? { totalLie: true as const } : {}),
    editable: typeof colonne.set === 'function',
    ...(colonne.kind === undefined ? {} : { kind: colonne.kind }),
    /* LE CHEMIN DE STOCKAGE PASSE, LUI AUSSI, EN DONNEE : c'est par lui que la
       fiche sait si un champ supporte d'etre vide (`proposeDetacher`,
       `views/core/RecordView.tsx`). Le rederiver cote vue redonnerait une
       seconde regle « natif ou sac », qui divergerait de `cheminDeStockage`. */
    ...(colonne.path === undefined ? {} : { path: colonne.path }),
    /* LE PLACEHOLDER ET L'OBLIGATION PASSENT AUSSI : ce sont les deux seules
       choses qui font qu'une case VIDE ne se rend pas pareil sur une fiche
       existante et sur un brouillon de creation. Sans eux, la fiche de creation
       dirait « Non renseigne » partout et ne saurait pas quelle case est
       obligatoire. */
    ...(colonne.placeholder === undefined ? {} : { placeholder: colonne.placeholder }),
    ...(colonne.required === true ? { required: true } : {}),
    ...(colonne.reference === undefined
      ? {}
      : {
          reference: {
            ...(colonne.reference.door === undefined ? {} : { door: colonne.reference.door }),
            family: colonne.reference.family,
            ...(colonne.reference.type === undefined ? {} : { type: colonne.reference.type }),
            ...(colonne.reference.modeles === true ? { modeles: true } : {}),
          },
        }),
  }));
}

/* ------------------------------------------------------------------ */
/* Les totaux : le pied du tableau, et le champ de total de la fiche   */
/* ------------------------------------------------------------------ */

/**
 * UN NOMBRE AFFICHE : separateurs du poste, deux decimales au plus.
 *
 * UN SEUL FORMAT POUR LES DEUX BOUTS DU MEME CHIFFRE : le pied d'une colonne
 * (fenetre ou serveur) et le champ de total d'une fiche montrent la meme somme,
 * lue au meme endroit. Deux formateurs auraient diverge au premier reglage, et
 * la fiche aurait affiche un autre nombre que son tableau.
 */
export function formaterNombre(valeur: number): string {
  return valeur.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** La clef du sac libre par laquelle les totaux SERVEUR arrivent au tableau. */
const CLEF_TOTAUX = 'totals';

/**
 * LE PIED D'UNE COLONNE : le total du SERVEUR quand l'hote en fournit un pour
 * cette clef, sinon le calcul sur la fenetre affichee.
 *
 * POURQUOI LES DEUX COEXISTENT. Le pied du tableau totalise « ce qu'on voit »,
 * et c'est juste sur une liste complete : la fenetre EST le jeu. Dans une carte
 * liee, plafonnee par construction, ce meme calcul affiche le total des 200
 * premieres lignes en le presentant comme LE total. Le symptome ne ressemble
 * pas a une panne, c'est un nombre plausible : d'ou un total demande au
 * serveur, et d'ou cette fonction, qui dit lequel des deux gagne.
 *
 * LE SERVEUR GAGNE DES QU'IL A PARLE, y compris avec un `null` : `average` rend
 * `null` quand aucune fiche ne repond, et retomber alors sur la fenetre
 * rendrait la moyenne des lignes chargees, c'est-a-dire l'inverse de ce que le
 * serveur vient de dire. Une clef ABSENTE du sac, elle, n'est pas une reponse :
 * l'ecran n'a rien demande, la fenetre reprend la main. C'est ce qui laisse les
 * ecrans existants (aucun `totals` dans leur sac) exactement comme avant.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : le choix est invisible a l'ecran (les deux
 * branches rendent un nombre), et seul un test peut le tenir.
 */
export function totalDePied(
  options: Record<string, unknown>,
  key: string,
  fenetre: string | null,
  /* L'HABILLAGE DE LA COLONNE, quand elle en a un : le total du SERVEUR s'ecrit
     comme celui de la fenetre, sinon deux chiffres du meme mot se liraient dans
     deux unites selon que la liste tient ou non sur une page. */
  format?: (valeur: number) => string,
): { texte: string | null; serveur: boolean; attente?: true } {
  const totaux = options[CLEF_TOTAUX];
  if (typeof totaux === 'object' && totaux !== null && key in totaux) {
    const valeur = (totaux as Record<string, unknown>)[key];
    /* L'INDEX DU TOTAL SE CONSTRUIT : on le DIT, a la place du chiffre (G16).
       Retomber sur le total de la fenetre afficherait un chiffre faux sous le
       mot « Total », sans rien signaler. */
    if (valeur === TOTAL_EN_CONSTRUCTION) return { texte: LIBELLE_INDEX, serveur: true, attente: true };
    if (typeof valeur === 'number' && Number.isFinite(valeur)) {
      return {
        texte: format === undefined ? formaterNombre(valeur) : format(valeur),
        serveur: true,
      };
    }
    /* `null` (une moyenne sans fiche) ou une valeur qui n'est pas un nombre :
       le serveur a repondu « rien a montrer », et on ne montre rien. */
    if (valeur === null) return { texte: null, serveur: true };
  }
  return { texte: fenetre, serveur: false };
}

/* ------------------------------------------------------------------ */
/* L'index en construction                                             */
/* ------------------------------------------------------------------ */

/**
 * UN TOTAL DONT L'INDEX N'EST PAS PRET. Decision du fondateur du 04/10/2026
 * (« auto », `docs/CHANTIERS-SOCLE.md`) : l'index qu'une carte liee ou un total
 * exige est CREE a la publication de l'app, et l'ecran dit « index en
 * construction » tant qu'il n'est pas pret, puis le total apparait seul.
 *
 * UN SEUL ETAT POUR « ABSENT » ET « EN CONSTRUCTION », et ce n'est pas une
 * paresse : pendant que l'index se construit, Firestore repond EXACTEMENT le
 * meme message que s'il n'existait pas (releve sur prod-tac-studio le
 * 04/10/2026, index en `CREATING`). Rien ne permet de les distinguer cote
 * client ; et un index absent est, par la decision, un index en route.
 */
export const TOTAL_EN_CONSTRUCTION = 'index-en-construction' as const;

/** Ce que le serveur rend pour un total : un nombre, rien, ou l'attente de l'index. */
export type TotalServeur = number | null | typeof TOTAL_EN_CONSTRUCTION;

/** Le mot de l'attente, partout le meme. */
export const LIBELLE_INDEX = 'Index en construction';

/** La raison, au survol ou sous le titre : ce qui se passe et ce qui va suivre. */
export const RAISON_INDEX =
  "Firestore construit l'index que cette lecture exige. Elle se relance seule et le resultat apparaitra des qu'il sera pret, en general en quelques minutes.";

/** Toutes les combien une lecture en attente d'index se relance, en ms. */
export const RELANCE_INDEX_MS = 30_000;

/**
 * L'ERREUR EST-ELLE UN INDEX QUI MANQUE ? Le SDK enrichit l'erreur de Firestore
 * en `MissingIndexError` (`packages/sdk/src/list-query.ts`) ; le socle ne
 * dependant pas du SDK, il la reconnait par sa FORME (son nom, ou le
 * `failed-precondition` brut de Firestore et son message). Toute autre panne
 * reste une panne.
 */
export function estErreurDIndex(erreur: unknown): boolean {
  if (erreur === null || typeof erreur !== 'object') return false;
  const { name, code, message } = erreur as { name?: unknown; code?: unknown; message?: unknown };
  if (name === 'MissingIndexError') return true;
  return code === 'failed-precondition' && typeof message === 'string' && /requires an index/i.test(message);
}

/** Le sac libre d'un binding, plus les totaux SERVEUR que la carte a demandes. */
export function sacAvecTotaux(
  options: Record<string, unknown> | undefined,
  totaux: Record<string, TotalServeur>,
): Record<string, unknown> {
  return { ...(options ?? {}), [CLEF_TOTAUX]: totaux };
}

/* ------------------------------------------------------------------ */
/* Creer depuis la fiche parente : les valeurs imposees                */
/* ------------------------------------------------------------------ */

/**
 * UNE VALEUR QU'UNE CREATION RECOIT D'OFFICE.
 *
 * D'OU ELLE VIENT : le « + Ajouter » d'une carte liee ne cree rien la ou il est
 * (la fenetre de creation appartient a l'ECRAN de l'app, pas au socle). Il
 * PASSE dans la porte liee et y ouvre SA creation, avec le champ du lien
 * pre-rempli et verrouille : c'est le bouton intelligent d'Odoo. Ce que la
 * porte d'arrivee recoit, c'est donc ca, et rien d'autre.
 *
 * `chemin` EST UN CHEMIN DE STOCKAGE (`assetId`, `customFields.materielId`),
 * jamais un nom nu : c'est le `by` du lien, tel que l'assemblage l'a pose
 * (`LinkedDoor.by`). La creation le range la ou il vivra, et rederiver la regle
 * ici la ferait exister a deux endroits.
 *
 * `libelle` NE SE STOCKE PAS, il s'AFFICHE : c'est le titre de la fiche parente
 * (« Sony FX9 »), ce que la fenetre montre dans la case verrouillee. « Un lien
 * stocke un id, jamais un libelle recopie » : ce mot ne part jamais au serveur.
 */
export interface ValeurImposee {
  /** Le CHEMIN DE STOCKAGE du champ impose, dans le jeu qu'on cree. */
  chemin: string;
  /** Ce qui s'ecrit : l'id de la fiche parente. */
  valeur: unknown;
  /** Ce qui s'affiche dans la case verrouillee, quand l'appelant le connait. */
  libelle?: string;
}

/**
 * POSE LES VALEURS IMPOSEES DANS UNE ENTREE DE CREATION.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est le seul endroit du geste qui puisse
 * se tromper en SILENCE. Un champ sur-mesure pose a la RACINE part au serveur,
 * passe la validation (le sac est libre), et la fiche creee n'apparait dans
 * AUCUNE carte liee : le cadrage cherche `customFields.materielId`, le document
 * porte `materielId`. Rien ne plante, rien ne s'affiche.
 *
 * DEUX ESPACES, ET DEUX SEULEMENT (AGENTS.md section 7) : le sac `customFields`
 * pour un chemin qui le nomme, la racine pour tout le reste. Un chemin plus
 * profond n'existe pas dans le socle, et l'inventer ici en ferait une regle que
 * personne d'autre ne tient.
 *
 * LE SAC EST RECOPIE CLEF PAR CLEF, jamais remplace : les autres champs
 * sur-mesure que le formulaire a deja saisis survivent. Et la valeur IMPOSEE
 * gagne sur celle que l'entree portait : c'est tout le sens du mot, et la case
 * est verrouillee a l'ecran pour la meme raison.
 *
 * NE MUTE RIEN : l'entree vient souvent d'un etat React que d'autres relisent.
 */
export function appliquerValeursImposees(
  entree: Record<string, unknown>,
  defaults: readonly ValeurImposee[] | undefined,
): Record<string, unknown> {
  if (defaults === undefined || defaults.length === 0) return entree;
  const sortie = { ...entree };
  for (const { chemin, valeur } of defaults) {
    if (!chemin.startsWith(SAC_SUR_MESURE)) {
      sortie[chemin] = valeur;
      continue;
    }
    const precedent = sortie['customFields'];
    const sac =
      typeof precedent === 'object' && precedent !== null && !Array.isArray(precedent)
        ? (precedent as Record<string, unknown>)
        : {};
    sortie['customFields'] = { ...sac, [chemin.slice(SAC_SUR_MESURE.length)]: valeur };
  }
  return sortie;
}

/* ------------------------------------------------------------------ */
/* Lier une fiche existante : le patch, et les candidates              */
/* ------------------------------------------------------------------ */

/**
 * LE PATCH D'UN RATTACHEMENT : UNE seule clef, rangee la ou le champ vit.
 *
 * `{ productId: 'p1' }` pour un champ natif, `{ customFields: { fournisseurId:
 * 'f1' } }` pour un champ sur-mesure. Le SDK eclate ensuite le sac en chemins
 * pointes et le FUSIONNE clef par clef (`buildUpdateData`,
 * `docs/DONNEES-SDK.md`) : les autres champs sur-mesure de la fiche survivent,
 * et une app n'efface jamais les clefs d'une autre.
 *
 * IL NE REECRIT PAS LA REGLE « natif ou sac », IL LA REUTILISE : c'est
 * exactement celle de la creation depuis une fiche parente, donc le meme
 * `appliquerValeursImposees`, sur une entree vide. Deux copies auraient
 * diverge, et le symptome est MUET des deux cotes : une fiche rattachee par sa
 * clef nue passe la validation et n'apparait dans AUCUNE carte liee, parce que
 * le cadrage, lui, cherche `customFields.fournisseurId`.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est le seul endroit du geste qui puisse
 * se tromper en silence, et un test ne peut la tenir que sans React ni SDK.
 */
export function patchDeRattachement(chemin: string, valeur: string): Record<string, unknown> {
  return appliquerValeursImposees({}, [{ chemin, valeur }]);
}

/**
 * CE QUE PORTE UNE CANDIDATE DEJA RATTACHEE AILLEURS, ecrit ici et nulle part
 * ailleurs.
 *
 * POURQUOI CETTE MENTION EXISTE. Une reference est A VALEUR UNIQUE : la lier
 * ici la DETACHE de la fiche ou elle est. Sans ce mot, le geste retire en
 * silence une ligne d'une AUTRE fiche, que personne ne regarde a ce
 * moment-la, et le defaut ne se decouvre qu'en ouvrant l'autre fiche.
 *
 * UN LIBELLE, PAS UNE PHRASE (G26), et il ne dit PAS ou : le nom de l'autre
 * parent demanderait un annuaire de plus pour un mot qu'on ne lit qu'une fois.
 */
export const MENTION_DEJA_LIEE = 'Déjà liée ailleurs';

/** Ce que le socle met entre le sous-titre d'une fiche et cette mention. */
const SEPARATEUR_MENTION = ' · ';

/** Une fiche que « Lier une fiche existante » propose. */
export interface CandidatARattacher {
  /** L'id de la fiche a ecrire : c'est elle qui recevra le champ du lien. */
  id: string;
  /** Son titre, tel que la porte LIEE l'affiche dans ses propres listes. */
  label: string;
  /** Son sous-titre, la mention « Deja liee ailleurs », ou les deux. */
  sousTitre?: string;
  /** Elle pointe DEJA une autre fiche : la lier la DEPLACE. */
  dejaLiee: boolean;
}

/**
 * LA VALEUR D'UN CHEMIN DE STOCKAGE dans un document, en chaine.
 *
 * MEME REGLE QUE `appliquerValeursImposees`, DANS L'AUTRE SENS : ce qui s'ECRIT
 * dans le sac se LIT dans le sac. Lire `record['fournisseurId']` sur un champ
 * sur-mesure rend toujours `undefined`, donc toutes les fiches passeraient pour
 * libres et « Deja liee ailleurs » ne s'afficherait JAMAIS.
 *
 * TOUT CE QUI N'EST PAS UNE CHAINE NON VIDE VAUT « pas rattachee » : un id est
 * une chaine, et un `null` laisse par un detachement (`deleteField` cote SDK,
 * ou une valeur vide posee a la main) dit exactement la meme chose.
 */
function valeurAuChemin(record: Record<string, unknown>, chemin: string): string {
  if (!chemin.startsWith(SAC_SUR_MESURE)) {
    const lue = record[chemin];
    return typeof lue === 'string' ? lue : '';
  }
  const sac = record['customFields'];
  if (typeof sac !== 'object' || sac === null || Array.isArray(sac)) return '';
  const lue = (sac as Record<string, unknown>)[chemin.slice(SAC_SUR_MESURE.length)];
  return typeof lue === 'string' ? lue : '';
}

/**
 * LES FICHES QU'ON PEUT RATTACHER A CETTE FICHE-CI, dans l'ordre du lecteur.
 *
 * TROIS SORTS, ET ILS NE SE CONFONDENT PAS :
 *   - DEJA LIEE A CE PARENT : elle est DEHORS. Elle est deja dans la carte,
 *     juste dessous : la proposer offrirait un geste sans effet, et le clic ne
 *     changerait rien a l'ecran (meme valeur ecrite), ce qui se lit comme une
 *     panne.
 *   - LIEE AILLEURS : elle est DEDANS, avec sa mention (voir
 *     `MENTION_DEJA_LIEE`). C'est un geste legitime (« cette personne travaille
 *     en fait dans cette societe-la »), mais il DEPLACE, et ca doit se voir
 *     avant le clic.
 *   - LIBRE : elle est dedans, nue.
 *
 * LES ARCHIVES SONT DEHORS, et ce n'est pas un doublon de ce que le serveur
 * fait deja. L'hote ne demande pas les archives (`archived` absent = les
 * actives seules, `ArgumentsDeListe`), donc elles n'arrivent normalement pas
 * jusqu'ici ; le filtre tient quand meme, parce que rattacher une fiche sortie
 * du parc est exactement la donnee qu'on passe ensuite du temps a expliquer
 * (meme regle que `choixDeFicheLiee`, `views/core/RecordView.tsx`). Il n'y a
 * pas d'exception « celle qui est deja la » ici, contrairement a une case de
 * fiche : ce choix ne montre AUCUNE valeur courante, il propose des fiches a
 * rattacher.
 *
 * `libeller` VIENT DE L'APPELANT, ET C'EST LA MEME PROJECTION QUE LES RANGEES :
 * la carte projette ses lignes avec le binding de la porte liee, et une
 * candidate doit porter le mot qu'elle portera une fois rattachee. Un libelle
 * invente ici (« le champ `name` ») aurait fait choisir dans une liste qui ne
 * ressemble pas au resultat.
 *
 * PAR ORDRE ALPHABETIQUE, casse ignoree : la fenetre arrive dans l'ordre
 * NATUREL du serveur (aucun `orderBy`, voir `ArgumentsDeListe`), et une liste
 * qu'on parcourt de l'oeil se range par son mot.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : les trois sorts se trompent en SILENCE
 * (une fiche qui manque ressemble a un jeu vide, une mention absente ne se
 * decouvre qu'en ouvrant l'autre fiche), et aucun build ne les compte.
 */
export function candidatsARattacher(
  records: readonly Record<string, unknown>[],
  lien: LinkedDoor,
  parentId: string,
  libeller: (record: Record<string, unknown>) => { title: string; subtitle?: string },
): CandidatARattacher[] {
  const candidats: CandidatARattacher[] = [];
  for (const record of records) {
    const id = record['id'];
    if (typeof id !== 'string' || id === '') continue;
    if (record['archivedAt'] != null) continue;
    /* `lien.by` EST DEJA UN CHEMIN DE STOCKAGE, pose par l'assemblage : on le
       prend TEL QUEL, exactement comme `cadrageDeLien`. */
    const attachee = valeurAuChemin(record, lien.by);
    if (attachee !== '' && attachee === parentId) continue;
    const { title, subtitle } = libeller(record);
    const dejaLiee = attachee !== '';
    const propre = subtitle === undefined || subtitle === '' ? '' : subtitle;
    /* LES DEUX SE CUMULENT plutot que de s'ecraser : le sous-titre de la porte
       liee est souvent ce qui SEPARE deux fiches de meme nom, et la mention est
       ce qui dit que le geste deplace. Perdre l'un pour montrer l'autre ferait
       choisir entre « laquelle » et « quel effet ». */
    const sousTitre = !dejaLiee
      ? propre
      : propre === ''
        ? MENTION_DEJA_LIEE
        : `${propre}${SEPARATEUR_MENTION}${MENTION_DEJA_LIEE}`;
    candidats.push({
      id,
      label: title,
      dejaLiee,
      ...(sousTitre === '' ? {} : { sousTitre }),
    });
  }
  return candidats.sort((a, b) =>
    a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }),
  );
}

/**
 * LES FICHES QUE « Lier une fiche existante » PROPOSE A UN CHAMP DE LIEN, le
 * sens DIRECT de `candidatsARattacher` (chantier `champ-lie-deux-gestes`).
 *
 * LA MEME REGLE, LUE DANS L'AUTRE SENS :
 *   - LA FICHE DEJA POSEE DANS LE CHAMP est DEHORS, comme une fiche deja liee a
 *     ce parent dans la carte : la choisir ne changerait rien, et un geste sans
 *     effet se lit comme une panne. Le champ la montre deja ;
 *   - LES ARCHIVES sont dehors (on ne rattache pas une fiche sortie du parc) ;
 *   - AUCUNE N'EST « DEJA LIEE AILLEURS » : un champ de lien est la reference de
 *     CETTE fiche, et vingt projets peuvent avoir le meme client sans que le
 *     choix de l'un deplace les autres. La mention n'a pas de sens ici.
 *
 * PAR ORDRE ALPHABETIQUE, casse ignoree, comme la carte.
 */
export function candidatsPourUnChamp(
  records: readonly Record<string, unknown>[],
  valeurEnPlace: string | null,
  libeller: (record: Record<string, unknown>) => { title: string; subtitle?: string },
): CandidatARattacher[] {
  const candidats: CandidatARattacher[] = [];
  for (const record of records) {
    const id = record['id'];
    if (typeof id !== 'string' || id === '') continue;
    if (record['archivedAt'] != null) continue;
    if (id === valeurEnPlace) continue;
    const { title, subtitle } = libeller(record);
    candidats.push({
      id,
      label: title,
      dejaLiee: false,
      ...(subtitle === undefined || subtitle === '' ? {} : { sousTitre: subtitle }),
    });
  }
  return candidats.sort((a, b) =>
    a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }),
  );
}
